import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

import { CandidateSuccessModal, FormDialog, InterviewSlots } from '@components';

import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';

import {
  geDateTimeInTimezone,
  isStatusLoading,
  normalizeCandidateStatus,
} from '@utils';
import { validationSchema } from './validationSchema';

import {
  cancelCandidateInterview,
  candidateDetailsSelectors,
  rescheduleCandidateInterview,
  setCandidateDetailsAction,
} from '@redux/candidateDetails';

import {
  CandidateDetailsAction,
  CandidateStatus,
  InterviewSlotOption,
  RESCHEDULE_REASONS,
  SearchParams,
} from '@constants';
import { getUserOffset } from '@utils';
import { CandidateRescheduleCancelFormValues, DateTimeRange } from '@types';

export const ClientRescheduleCancelModal = () => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>();
  const [, setSearchParams] = useSearchParams();

  const {
    cancelCandidateInterviewAPIStatus,
    rescheduleCandidateInterviewAPIStatus,
    writeError,
    data,
  } = useAppSelector(candidateDetailsSelectors.getCandidateDetailsData);
  const candidateDetailsAction = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsAction,
  );

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState<boolean>(false);
  const candidateStatus = normalizeCandidateStatus(data?.status);

  const latestCI = data?.interview?.at(-1) || null;
  const slots: DateTimeRange[] = [];
  const isCancelling =
    candidateDetailsAction === CandidateDetailsAction.CANCEL_INTERVIEW;
  const header = {
    label: isCancelling ? 'Cancel interview' : 'Reschedule interview',
    subLabel: [data?.profile?.firstName, data?.profile?.lastName]
      .filter(Boolean)
      .join(' '),
  };
  const [successHeader, setSuccessHeader] = useState(header);

  const bookedStart = latestCI?.scheduledSlot?.[0]?.start || null;
  const booked = bookedStart
    ? geDateTimeInTimezone(
        bookedStart,
        latestCI?.clientTimezone || getUserOffset(),
      ).format('ddd, MMM DD, hh:mm a')
    : null;

  const isLoading =
    isStatusLoading(cancelCandidateInterviewAPIStatus) ||
    isStatusLoading(rescheduleCandidateInterviewAPIStatus);

  const formik = useFormik<CandidateRescheduleCancelFormValues>({
    initialValues: {
      interviewSlots: null,
      interviewOption: null,
      clientCalendar: null,
      details: null,
      clientTimezone: latestCI?.clientTimezone || getUserOffset(),
      isCancelled: false,
      rescheduleReason: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (data?.id && values.isCancelled) {
        const res = await dispatch(
          cancelCandidateInterview({
            candidateId: data.id,
            details: values.details,
          }),
        );
        if (cancelCandidateInterview.fulfilled.match(res)) {
          handleModalClose();
          setSuccessHeader(header);
          setSuccessModalOpen(true);
        }
      } else if (data?.id) {
        const res = await dispatch(
          rescheduleCandidateInterview({
            candidateId: data.id,
            details: [values.rescheduleReason, values.details]
              .filter(Boolean)
              .join(' — '),
            clientCalendar: values.clientCalendar,
            interviewSlots: values.interviewSlots,
          }),
        );
        if (rescheduleCandidateInterview.fulfilled.match(res)) {
          handleModalClose();
          setSuccessHeader(header);
          setSuccessModalOpen(true);
        }
      }
    },
  });

  useEffect(() => {
    if (candidateDetailsAction) {
      if (
        [
          CandidateDetailsAction.RESCHEDULE_INTERVIEW,
          CandidateDetailsAction.CANCEL_INTERVIEW,
        ].includes(candidateDetailsAction)
      ) {
        const cancelling =
          candidateDetailsAction === CandidateDetailsAction.CANCEL_INTERVIEW;
        formik.setFieldValue('isCancelled', cancelling);
        formik.setFieldValue(
          'interviewOption',
          cancelling ? null : InterviewSlotOption.Date,
        );
        setModalOpen(true);
      }
    } else {
      formik.resetForm();
      setModalOpen(false);
    }
  }, [candidateDetailsAction]);

  const handleModalClose = () => {
    dispatch(setCandidateDetailsAction(null));
    setSearchParams(
      (_searchParams) => {
        _searchParams.delete(SearchParams.ACTION);
        return _searchParams;
      },
      { replace: true },
    );
  };

  const handleSlotChange = (newSlot: DateTimeRange | null) => {
    formik.setValues({
      ...formik.values,
      isCancelled: false,
      interviewSlots: newSlot ? [newSlot] : null,
      clientCalendar: null,
    });
    inputRef.current?.focus();
  };

  const handleOptionChange = (option: InterviewSlotOption) => {
    formik.setValues({
      ...formik.values,
      interviewOption: option,
      interviewSlots: null,
      clientCalendar: null,
      isCancelled: false,
    });
  };

  const interviewSlotsError =
    ((formik.submitCount || formik.touched.interviewSlots) &&
      formik.errors.interviewSlots) ||
    '';

  const clientCalendarError =
    ((formik.submitCount || formik.touched.clientCalendar) &&
      formik.errors.clientCalendar) ||
    '';

  const detailsError =
    ((formik.submitCount || formik.touched.details) && formik.errors.details) ||
    '';

  const isReasonMissing = isCancelling && !formik.values.details?.trim();

  const missing = isReasonMissing
    ? 'Say why the interview is off to submit.'
    : !isCancelling &&
        !formik.values.interviewSlots?.some((slot) => slot.start && slot.end) &&
        !formik.values.clientCalendar
      ? 'Pick a new time or share your calendar to submit.'
      : '';

  if (!data) return null;

  return (
    <>
      <FormDialog
        isOpen={isModalOpen}
        onClose={handleModalClose}
        {...header}
        submitBtnLabel={
          isCancelling ? 'Cancel interview' : 'Request a new time'
        }
        cancelBtnLabel={isCancelling ? 'Keep it booked' : undefined}
        onSubmit={formik.handleSubmit}
        isLoading={isLoading}
        submitDisabled={Boolean(missing)}
        hint={missing}
        error={writeError}
      >
        {!!booked && (
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              mb: 2,
              borderRadius: 1,
              backgroundColor: 'text.linkLight',
            }}
          >
            <Typography variant="body1" fontWeight={500}>
              Booked for {booked}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isCancelling
                ? 'AgileEngine lets the candidate know this slot is off.'
                : 'This slot stays booked until AgileEngine agrees a new one with the candidate.'}
            </Typography>
          </Box>
        )}
        {!isCancelling && (
          <>
            <Box display="flex" gap={2} mb={1}>
              <Typography variant="body2" color="text.secondary">
                Pick a new time
              </Typography>
              <Typography variant="body2" color="text.secondary">
                UTC {formik.values.clientTimezone}
              </Typography>
            </Box>
            <Box
              p={2}
              borderRadius={1}
              bgcolor="background.backgroundPrimary"
              mb={2}
            >
              <InterviewSlots
                slots={slots}
                selectedSlot={formik.values.interviewSlots?.[0] || null}
                clientCalendar={formik.values.clientCalendar}
                candidateReadyAnytime={latestCI?.candidateReadyAnytime}
                clientTimezone={formik.values.clientTimezone}
                interviewOption={formik.values.interviewOption}
                error={
                  (typeof interviewSlotsError === 'string' &&
                    interviewSlotsError) ||
                  clientCalendarError
                }
                setInterviewOption={handleOptionChange}
                setSelectedSlot={handleSlotChange}
                setClientCalendar={(value) =>
                  formik.setFieldValue('clientCalendar', value)
                }
                deleteSelectedSlot={() => {
                  formik.setValues({
                    ...formik.values,
                    interviewOption: null,
                    interviewSlots: null,
                    clientCalendar: null,
                    isCancelled: false,
                  });
                }}
              />
            </Box>
          </>
        )}
        <Stack gap={2}>
          {!isCancelling && (
            <TextField
              select
              fullWidth
              name="rescheduleReason"
              label="Reason"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              value={formik.values.rescheduleReason || ''}
              onChange={(event) =>
                formik.setFieldValue('rescheduleReason', event.target.value)
              }
            >
              <MenuItem value="">Not specified</MenuItem>
              {RESCHEDULE_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            minRows={3}
            required
            name="details"
            label={isCancelling ? 'Reason for cancelling' : 'Notes'}
            placeholder={
              isCancelling
                ? 'Why is this interview not going ahead?'
                : 'What is making the booked time not work?'
            }
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            value={formik.values.details || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={detailsError}
            error={!!detailsError}
          />
        </Stack>
      </FormDialog>
      <CandidateSuccessModal
        header={successHeader}
        isOpen={isSuccessModalOpen}
        isPassed={candidateStatus === CandidateStatus.CallRequested}
        label={
          candidateStatus === CandidateStatus.CallRequested
            ? "Thanks, we'll set up an interview"
            : 'Thank you for your response!'
        }
        subLabel={'Our Recruiter may get back to you to clarify details.'}
        handleClose={() => setSuccessModalOpen(false)}
      />
    </>
  );
};
